/**
 * frontend/src/components/HoursInput.jsx
 *
 * Hours and minutes, stored as decimal hours.
 *
 * The database column is numeric(8,2) in hours, and every rollup in the
 * system sums it, so that does not change. This only stops people doing the
 * arithmetic in their heads: 1h 45m rather than 1.75.
 *
 * The conversion lives here so the two fields that use it cannot drift, and
 * so anywhere else that shows hours can format them the same way.
 */
import { Clock } from 'lucide-react';

/** Decimal hours to whole hours and minutes. 1.75 -> { h: 1, m: 45 } */
export const hoursToHM = (dec) => {
  const n = Math.max(0, Number(dec) || 0);
  const total = Math.round(n * 60);          // 2 decimal places is finer than a minute
  return { h: Math.floor(total / 60), m: total % 60 };
};

/** Hours and minutes to decimal hours, rounded to what the column stores. */
export const hmToHours = (h, m) => {
  const total = (Number(h) || 0) * 60 + (Number(m) || 0);
  return Math.round((total / 60) * 100) / 100;
};

/** For display: 1.75 -> "1h 45m", 2 -> "2h", 0 -> "0h". */
export const formatHM = (dec) => {
  const { h, m } = hoursToHM(dec);
  if (!h && !m) return '0h';
  return m ? `${h}h ${m}m` : `${h}h`;
};

const isBlank = (v) => v === null || v === undefined || v === '';

export default function HoursInput({ value, onChange, disabled = false, id }) {
  const blank = isBlank(value);
  const { h, m } = hoursToHM(value);

  /* Minutes are not clamped to 59 on purpose: typing 90 gives 1h 30m, which
     is what somebody entering a long session means. The conversion
     normalises it and the fields redraw from the stored decimal. */
  const emit = (nh, nm) => onChange(hmToHours(nh, nm));

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-1">
        <input id={id} type="number" min="0" step="1" disabled={disabled}
          className="input text-sm" style={{ width: 72 }}
          value={blank ? '' : h} placeholder="0"
          onChange={(e) => emit(e.target.value, blank ? 0 : m)} />
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>h</span>
      </div>
      <div className="flex items-center gap-1">
        <input type="number" min="0" step="5" disabled={disabled}
          className="input text-sm" style={{ width: 72 }}
          value={blank ? '' : m} placeholder="0"
          onChange={(e) => emit(blank ? 0 : h, e.target.value)} />
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>m</span>
      </div>
      {!blank && (
        <span className="text-xs flex items-center gap-1 ml-1" style={{ color: 'var(--text-secondary)' }}>
          <Clock size={12} /> {Number(value).toFixed(2)}h
        </span>
      )}
    </div>
  );
}
